import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  Timestamp,
  deleteDoc,
  doc,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { normalizeCommentReactions, toggleCommentReaction } from '../../utils/commentReactions';
import CommentReactions from '../CommentReactions';
import {
  Paperclip,
  Trash2,
  Send,
  Plus,
  FileText,
  Image,
  X,
  CornerDownRight,
  Reply as ReplyIcon,
} from 'lucide-react';
import { auth, db, storage } from '../../utils/firebase';
import type { MarketingRequestComment, MarketingRequestStatus } from '../../types/marketing';
import { CommentAttachment, CRMUser, TaskCommentKind } from '../../types';
import { useAuth } from '../../utils/AuthContext';
import { useCRM } from '../../utils/CRMContext';
import { formatDistanceToNow } from 'date-fns';
import { canDeleteMarketingRequestComment } from '../../utils/commentPermissions';
import { isInternalCommentMentionUser } from '../../utils/mentionAffiliation';
import { filterOpsBotFromMentions } from '../../utils/opsBotMentionAccess';
import { buildCommentAttachmentStorageFileName } from '../../utils/storageFileNames';
import {
  buildOpsAttachmentDocumentId,
  effectiveOpsAttachmentMime,
  opsAttachmentDisplayName,
  opsAttachmentViewerKind,
} from '../../utils/opsAttachmentViewer';
import {
  buildTaskCommentsById,
  getTaskCommentThreadRootId,
  groupTaskCommentRepliesByThreadRoot,
} from '../../utils/taskCommentThread';
import { useCommentTyping } from '../../hooks/useCommentTyping';
import { commentTypingThreadKey } from '../../utils/commentTypingService';
import CommentTypingIndicator from '../pm/CommentTypingIndicator';
import RichCommentEditor, { RichCommentEditorHandle, RichCommentEditorPayload } from '../pm/RichCommentEditor';
import RichCommentRenderer from '../pm/RichCommentRenderer';
import QuestionAnswerSlot from '../pm/QuestionAnswerSlot';
import CommentsOpenHelper from '../pm/CommentsOpenHelper';
import '../pm/CommentsOpenHelper.css';
import '../pm/TaskComments.css';
import UserHoverCard from '../ui/UserHoverCard';
import {
  hasSeenCommentsOpenHelper,
  markCommentsOpenHelperSeen,
} from '../../utils/commentsOpenHelper';

interface MarketingRequestCommentsProps {
  requestId: string;
  requestTitle?: string;
  requestStatus?: MarketingRequestStatus;
  /** marketingRequestComments doc id — scroll + highlight */
  highlightCommentId?: string | null;
  onHighlightConsumed?: () => void;
  popoutRequestToken?: number | string | null;
  onPopoutClose?: () => void;
  /** When true, render only the comment feed/composer (parent owns popout chrome). */
  embedded?: boolean;
  onCommentCountChange?: (count: number) => void;
  onOpenPdf?: (url: string, title: string, documentId: string, options?: { annotationId?: string; pageNumber?: number }) => void;
  onOpenImage?: (url: string, title: string, documentId: string, options?: { annotationId?: string; mimeType?: string }) => void;
  onOpenCsv?: (url: string, title: string, documentId: string, options?: { annotationId?: string }) => void;
}

type FilterId = 'all' | 'mentions' | 'mine' | 'attachments';

const SCOPE_FILTER_CHIPS: { id: Exclude<FilterId, 'mine'>; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'mentions', label: '@ Me' },
  { id: 'attachments', label: 'Has files' },
];

const KIND_OPTIONS: { id: TaskCommentKind; label: string; short: string }[] = [
  { id: 'note', label: 'Note', short: 'Note' },
  { id: 'decision', label: 'Decision', short: 'Decision' },
  { id: 'question', label: 'Question', short: 'Question' },
  { id: 'action', label: 'Action item', short: 'Action' },
];

const draftKey = (uid: string | undefined, requestId: string) => `mreqDraft:${uid || 'anon'}:${requestId}`;

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const getFileIcon = (type: string): React.ReactNode => {
  if (type.startsWith('image/')) return <Image size={14} />;
  if (type === 'application/pdf') return <FileText size={14} />;
  return <Paperclip size={14} />;
};

function mapMarketingRequestCommentDoc(d: QueryDocumentSnapshot<DocumentData>): MarketingRequestComment {
  const data = d.data();
  return {
    id: d.id,
    ...data,
    reactions: normalizeCommentReactions(data.reactions),
    createdAt: data.createdAt?.toDate?.() || new Date(),
    updatedAt: data.updatedAt?.toDate?.(),
  } as MarketingRequestComment;
}

/** Adapt ops comments for shared thread helpers (id + parentId only). */
function asThreadComments(comments: MarketingRequestComment[]) {
  return comments.map(c => ({
    id: c.id,
    parentId: c.parentId ?? null,
    createdAt: c.createdAt,
    authorId: c.userId,
    authorName: c.userName,
    body: c.text,
    taskId: c.requestId,
  }));
}

function excerptOf(comment: MarketingRequestComment, max = 80): string {
  const src = comment.text || '';
  const oneLine = src.replace(/\s+/g, ' ').trim();
  return oneLine.length > max ? `${oneLine.slice(0, max)}…` : oneLine;
}

const MarketingRequestComments: React.FC<MarketingRequestCommentsProps> = ({
  requestId,
  requestTitle,
  requestStatus,
  highlightCommentId,
  onHighlightConsumed,
  popoutRequestToken = null,
  onPopoutClose,
  embedded = false,
  onCommentCountChange,
  onOpenPdf,
  onOpenImage,
  onOpenCsv,
}) => {
  const commentsLocked = requestStatus === 'done' || requestStatus === 'cancelled';
  const [comments, setComments] = useState<MarketingRequestComment[]>([]);
  const [isPopout, setIsPopout] = useState(false);
  const [showCommentsOpenHelper, setShowCommentsOpenHelper] = useState(false);
  const [draftHtml, setDraftHtml] = useState('');
  const [initialDraftHtml, setInitialDraftHtml] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileDragOver, setFileDragOver] = useState(false);
  const fileDragDepthRef = useRef(0);
  const [filter, setFilter] = useState<FilterId>('all');
  const [kindFilter, setKindFilter] = useState<TaskCommentKind | 'any'>('any');
  const [mineAuthorId, setMineAuthorId] = useState('');
  const [composerKind, setComposerKind] = useState<TaskCommentKind | null>(null);
  const [replyTo, setReplyTo] = useState<MarketingRequestComment | null>(null);

  const { user } = useAuth();
  const typingThreadKey = useMemo(() => commentTypingThreadKey('marketing', requestId), [requestId]);
  const typingLabel = useCommentTyping(typingThreadKey, draftHtml, !commentsLocked);
  const { crmUsers } = useCRM();
  const editorRef = useRef<RichCommentEditorHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollFeedToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const scheduleScrollFeedToBottom = useCallback(() => {
    scrollFeedToBottom();
    requestAnimationFrame(() => {
      scrollFeedToBottom();
      requestAnimationFrame(scrollFeedToBottom);
    });
  }, [scrollFeedToBottom]);

  const mentionableUsers = useMemo(() => {
    const viewer = crmUsers.find((u) => u.uid === user?.id);
    const internals = crmUsers.filter((u) => isInternalCommentMentionUser(u));
    return filterOpsBotFromMentions(internals, {
      role: viewer?.role ?? user?.role,
      designations: viewer?.designations,
    });
  }, [crmUsers, user?.id, user?.role]);

  const photoForUid = (uid: string) => crmUsers.find(x => x.uid === uid)?.photoURL;

  useEffect(() => {
    if (embedded || popoutRequestToken == null) return;
    setIsPopout(true);
  }, [popoutRequestToken, embedded]);

  useEffect(() => {
    onCommentCountChange?.(comments.length);
  }, [comments.length, onCommentCountChange]);

  const onPopoutCloseRef = useRef(onPopoutClose);
  onPopoutCloseRef.current = onPopoutClose;
  const wasPopoutRef = useRef(false);
  useEffect(() => {
    if (wasPopoutRef.current && !isPopout) {
      onPopoutCloseRef.current?.();
    }
    wasPopoutRef.current = isPopout;
  }, [isPopout]);

  useEffect(() => {
    if (!isPopout) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        setIsPopout(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('tc-popout-open');
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('tc-popout-open');
    };
  }, [isPopout]);

  const commentsPopoutOpen = embedded || isPopout;

  const dismissCommentsOpenHelper = useCallback(() => {
    markCommentsOpenHelperSeen();
    setShowCommentsOpenHelper(false);
  }, []);

  useEffect(() => {
    if (!commentsPopoutOpen) {
      setShowCommentsOpenHelper(false);
      return;
    }
    if (hasSeenCommentsOpenHelper() || highlightCommentId) return;
    const timer = window.setTimeout(() => setShowCommentsOpenHelper(true), 320);
    return () => window.clearTimeout(timer);
  }, [commentsPopoutOpen, requestId, popoutRequestToken, highlightCommentId]);

  useEffect(() => {
    if (!requestId || !user?.id) return;
    try {
      const saved = window.localStorage.getItem(draftKey(user.id, requestId));
      if (saved && saved !== '<p></p>') {
        setInitialDraftHtml(saved);
        setDraftHtml(saved);
      } else {
        setInitialDraftHtml('');
        setDraftHtml('');
      }
    } catch {
      /* ignore */
    }
  }, [requestId, user?.id]);

  useEffect(() => {
    if (!user?.id || !requestId) return;
    const handle = window.setTimeout(() => {
      try {
        if (draftHtml && draftHtml !== '<p></p>') {
          window.localStorage.setItem(draftKey(user.id, requestId), draftHtml);
        } else {
          window.localStorage.removeItem(draftKey(user.id, requestId));
        }
      } catch {
        /* ignore */
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [draftHtml, requestId, user?.id]);

  useEffect(() => {
    if (!requestId) return;
    const q = query(
      collection(db, 'marketingRequestComments'),
      where('requestId', '==', requestId),
      orderBy('createdAt', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      snap => setComments(snap.docs.map(mapMarketingRequestCommentDoc)),
      err => console.error('[MarketingRequestComments] Failed to listen to opsComments:', err),
    );
    return unsub;
  }, [requestId]);

  useLayoutEffect(() => {
    if (highlightCommentId) return;
    scrollFeedToBottom();
  }, [comments.length, highlightCommentId, scrollFeedToBottom]);

  useLayoutEffect(() => {
    if (highlightCommentId) return;
    if (!embedded && !isPopout) return;
    scheduleScrollFeedToBottom();
    const timers = [80, 200, 400].map((ms) => window.setTimeout(scrollFeedToBottom, ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [
    embedded,
    isPopout,
    popoutRequestToken,
    requestId,
    highlightCommentId,
    comments.length,
    scheduleScrollFeedToBottom,
    scrollFeedToBottom,
  ]);

  useLayoutEffect(() => {
    if (!highlightCommentId) return;
    const match = comments.find(x => x.id === highlightCommentId);
    if (!match) {
      if (comments.length > 0) onHighlightConsumed?.();
      return;
    }
    const el = document.getElementById(`mreq-comment-${highlightCommentId}`);
    if (!el) {
      onHighlightConsumed?.();
      return;
    }
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('tc-item--flash');
    });
    const t = window.setTimeout(() => {
      el.classList.remove('tc-item--flash');
      onHighlightConsumed?.();
    }, 2200);
    return () => {
      window.clearTimeout(t);
      el.classList.remove('tc-item--flash');
    };
  }, [highlightCommentId, comments, onHighlightConsumed]);

  useEffect(() => {
    setFilter('all');
    setKindFilter('any');
    setMineAuthorId('');
    setReplyTo(null);
  }, [requestId]);

  const authorFilterOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const c of comments) {
      if (c.userId && !seen.has(c.userId)) {
        seen.set(c.userId, c.userName || c.userId.slice(0, 8));
      }
    }
    return Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [comments]);

  const commentMatchesFilter = useCallback(
    (c: MarketingRequestComment): boolean => {
      if (kindFilter !== 'any' && c.kind !== kindFilter) return false;
      const mineUid = (mineAuthorId || user?.id || '').trim();
      switch (filter) {
        case 'mentions':
          return Array.isArray(c.mentions) && c.mentions.some(m => m.userId === user?.id);
        case 'mine':
          return String(c.userId ?? '') === String(mineUid);
        case 'attachments':
          return Array.isArray(c.attachments) && c.attachments.length > 0;
        case 'all':
        default:
          return true;
      }
    },
    [filter, kindFilter, mineAuthorId, user?.id],
  );

  const threadComments = useMemo(() => asThreadComments(comments), [comments]);
  const repliesByThreadRoot = useMemo(
    () => groupTaskCommentRepliesByThreadRoot(threadComments as never),
    [threadComments],
  );

  const visibleTopLevelComments = useMemo(() => {
    return comments
      .filter(c => !c.parentId)
      .filter(c => {
        if (commentMatchesFilter(c)) return true;
        const threadReplies = c.id ? repliesByThreadRoot.get(c.id) || [] : [];
        return threadReplies.some(reply => {
          const full = comments.find(x => x.id === reply.id);
          return full && commentMatchesFilter(full);
        });
      });
  }, [comments, commentMatchesFilter, repliesByThreadRoot]);

  const addFilesFromList = useCallback((fileList: FileList | File[]) => {
    if (commentsLocked) return;
    const files = Array.from(fileList);
    const validFiles = files.filter(f => {
      if (f.size > 100 * 1024 * 1024) {
        alert(`${f.name} exceeds 100MB limit.`);
        return false;
      }
      return true;
    });
    if (validFiles.length === 0) return;
    setAttachments(prev => [...prev, ...validFiles]);
  }, [commentsLocked]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFilesFromList(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const hasFilesInTransfer = (dt: DataTransfer) =>
    Array.from(dt.types || []).includes('Files');

  const handleFileDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (commentsLocked || !hasFilesInTransfer(e.dataTransfer)) return;
    fileDragDepthRef.current += 1;
    if (fileDragDepthRef.current === 1) setFileDragOver(true);
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current -= 1;
    if (fileDragDepthRef.current <= 0) {
      fileDragDepthRef.current = 0;
      setFileDragOver(false);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasFilesInTransfer(e.dataTransfer)) e.dataTransfer.dropEffect = 'copy';
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    fileDragDepthRef.current = 0;
    setFileDragOver(false);
    if (e.dataTransfer.files?.length) addFilesFromList(e.dataTransfer.files);
  };

  const uploadAttachments = useCallback(async (files: File[]): Promise<CommentAttachment[]> => {
    await auth.authStateReady();
    const firebaseUser = auth.currentUser;
    if (!firebaseUser || firebaseUser.isAnonymous) {
      throw new Error(
        'Firebase sign-in required for file uploads. Use your company Google or email sign-in (anonymous sessions cannot upload to Storage).',
      );
    }

    const uploaded: CommentAttachment[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const objectName = buildCommentAttachmentStorageFileName(fileId, file.name);
      const filePath = `marketing-request-comments/${requestId}/${objectName}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file, {
        contentType: file.type || 'application/octet-stream',
      });
      const url = await getDownloadURL(storageRef);
      uploaded.push({ id: fileId, name: file.name, url, type: file.type, size: file.size });
      setUploadProgress(((i + 1) / files.length) * 100);
    }
    return uploaded;
  }, [requestId]);

  const submitNewComment = useCallback(
    async (
      payload?: RichCommentEditorPayload,
      options?: { replyToOverride?: MarketingRequestComment | null; source?: 'composer' | 'answer' },
    ) => {
      const isAnswer = options?.source === 'answer';
      const editorPayload = payload ?? editorRef.current?.getPayload();
      if (!editorPayload) return;
      if ((editorPayload.isEmpty && (isAnswer || attachments.length === 0)) || !user || isSubmitting || commentsLocked) return;

      if (attachments.length > 0 && !auth.currentUser) {
        window.alert(
          'Your session is not ready to upload files. Please refresh the page and try again. If this keeps happening, sign out and sign back in.',
        );
        return;
      }

      setIsSubmitting(true);
      setUploadProgress(0);

      try {
        let uploadedAttachments: CommentAttachment[] = [];
        if (!isAnswer && attachments.length > 0) {
          await auth.currentUser!.getIdToken(true);
          uploadedAttachments = await uploadAttachments(attachments);
        }

        const userName = crmUsers.find(u => u.uid === user.id)?.displayName || user.name || user.email || 'Unknown';
        const docPayload: Record<string, unknown> = {
          requestId,
          userId: user.id,
          userName,
          userInitials: getInitials(userName),
          text: editorPayload.bodyPlain,
          bodyHtml: editorPayload.bodyHtml,
          bodyFormat: 'html',
          mentions: editorPayload.mentions.length > 0 ? editorPayload.mentions : null,
          attachments: uploadedAttachments.length > 0 ? uploadedAttachments : null,
          createdAt: Timestamp.now(),
        };

        const effectiveReply = options && 'replyToOverride' in options ? options.replyToOverride : replyTo;
        if (effectiveReply?.id) {
          const byId = buildTaskCommentsById(threadComments as never);
          docPayload.parentId = getTaskCommentThreadRootId(
            { id: effectiveReply.id, parentId: effectiveReply.parentId ?? null },
            byId,
          ) || effectiveReply.id;
        }
        if (!isAnswer && composerKind) docPayload.kind = composerKind;

        await addDoc(collection(db, 'marketingRequestComments'), docPayload);

        if (!isAnswer) {
          editorRef.current?.clear();
          setDraftHtml('');
          setInitialDraftHtml('');
          try {
            window.localStorage.removeItem(draftKey(user?.id, requestId));
          } catch {
            /* ignore */
          }
          setAttachments([]);
          setReplyTo(null);
          setComposerKind(null);
          setUploadProgress(0);
        }
        requestAnimationFrame(() => scrollFeedToBottom());
      } catch (error: unknown) {
        console.error('Error adding ops comment:', error);
        const code = typeof error === 'object' && error && 'code' in error ? String((error as { code: string }).code) : '';
        const message = error instanceof Error ? error.message : String(error);
        window.alert(
          attachments.length > 0
            ? `Comment was not sent. File upload failed (${code || 'error'}): ${message}`
            : `Could not send comment (${code || 'error'}): ${message}`,
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      attachments,
      commentsLocked,
      composerKind,
      crmUsers,
      isSubmitting,
      replyTo,
      requestId,
      threadComments,
      uploadAttachments,
      user,
      scrollFeedToBottom,
    ],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitNewComment();
  };

  const handleEditorSubmitShortcut = useCallback(
    (payload: RichCommentEditorPayload) => {
      void submitNewComment(payload);
    },
    [submitNewComment],
  );

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await deleteDoc(doc(db, 'marketingRequestComments', commentId));
    } catch (error) {
      console.error('Error deleting ops comment:', error);
    }
  };

  const getUserDisplayName = useCallback(
    (id: string) => crmUsers.find(u => u.uid === id)?.displayName || id.slice(0, 8),
    [crmUsers],
  );

  const toggleMarketingRequestCommentReaction = useCallback(
    async (commentId: string, emoji: string) => {
      if (!user?.id) return;
      try {
        await toggleCommentReaction(db, doc(db, 'marketingRequestComments', commentId), emoji, user.id);
      } catch (error) {
        console.error('Error toggling reaction:', error);
      }
    },
    [user?.id],
  );

  const renderAttachments = (atts: CommentAttachment[]) => (
    <div className="tc-attachments">
      {atts.map(att => {
        const docId = buildOpsAttachmentDocumentId(requestId, att.id);
        const displayName = opsAttachmentDisplayName(att);
        const mime = effectiveOpsAttachmentMime(att);
        const kind = opsAttachmentViewerKind(att);
        if (kind === 'pdf' && onOpenPdf) {
          return (
            <button
              key={att.id}
              type="button"
              className="tc-attachment-chip"
              title={`${displayName} (${formatFileSize(att.size)})`}
              onClick={() => onOpenPdf(att.url, displayName, docId)}
            >
              <span className="tc-attachment-icon">{getFileIcon(mime)}</span>
              <span className="tc-attachment-name">{displayName}</span>
            </button>
          );
        }
        if (kind === 'image' && onOpenImage) {
          return (
            <button
              key={att.id}
              type="button"
              className="tc-attachment-chip"
              title={`${displayName} (${formatFileSize(att.size)})`}
              onClick={() => onOpenImage(att.url, displayName, docId, { mimeType: mime })}
            >
              <span className="tc-attachment-icon">{getFileIcon(mime)}</span>
              <span className="tc-attachment-name">{displayName}</span>
            </button>
          );
        }
        if (kind === 'csv' && onOpenCsv) {
          return (
            <button
              key={att.id}
              type="button"
              className="tc-attachment-chip"
              title={`${displayName} (${formatFileSize(att.size)})`}
              onClick={() => onOpenCsv(att.url, displayName, docId)}
            >
              <span className="tc-attachment-icon">{getFileIcon(mime)}</span>
              <span className="tc-attachment-name">{displayName}</span>
            </button>
          );
        }
        return (
          <a
            key={att.id}
            href={att.url}
            target="_blank"
            rel="noopener noreferrer"
            className="tc-attachment-chip"
            title={`${displayName} (${formatFileSize(att.size)})`}
          >
            <span className="tc-attachment-icon">{getFileIcon(mime)}</span>
            <span className="tc-attachment-name">{displayName}</span>
          </a>
        );
      })}
    </div>
  );

  const kindMeta = (k?: TaskCommentKind) => KIND_OPTIONS.find(o => o.id === k);

  const renderCommentItem = (c: MarketingRequestComment, options: { indent?: boolean; idx: number }) => {
    const { indent, idx } = options;
    const isOwn = String(c.userId ?? '') === String(user?.id ?? '');
    const km = kindMeta(c.kind);
    const canModerate = !!user && canDeleteMarketingRequestComment(user, c);

    return (
      <div
        key={c.id}
        id={c.id ? `mreq-comment-${c.id}` : undefined}
        className={`tc-item ${isOwn ? 'tc-item-own' : ''}${indent ? ' tc-item--reply' : ''}`}
        style={{ animationDelay: `${idx * 30}ms` }}
      >
        {indent && <span className="tc-thread-line" aria-hidden />}
        <UserHoverCard
          className="tc-avatar"
          name={c.userName}
          photoURL={photoForUid(c.userId)}
          userId={c.userId}
          crmUser={crmUsers.find(u => u.uid === c.userId)}
        />
        <div className="tc-item-body">
          <div className="tc-item-meta">
            <span className="tc-item-author">{c.userName}</span>
            {km && (
              <span className={`tc-kind-badge tc-kind-badge--${km.id}`} title={`${km.label} comment`}>
                {km.short}
              </span>
            )}
            <span className="tc-item-time">{formatDistanceToNow(c.createdAt, { addSuffix: true })}</span>
            <div className="tc-item-actions">
              {!commentsLocked && c.id && (
                <button
                  type="button"
                  className="tc-item-iconbtn"
                  onClick={() => setReplyTo(c)}
                  title="Reply"
                  aria-label="Reply"
                >
                  <ReplyIcon size={11} />
                </button>
              )}
              {canModerate && c.id && (
                <button
                  type="button"
                  className="tc-item-delete"
                  onClick={() => handleDelete(c.id!)}
                  title={isOwn ? 'Delete your comment' : 'Delete comment (moderator)'}
                >
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          </div>
          <div className="tc-item-text">
            <RichCommentRenderer bodyHtml={c.bodyHtml} body={c.text} />
          </div>
          {user && c.id && (
            <CommentReactions
              classPrefix="tc-"
              reactions={c.reactions || {}}
              currentUserId={user.id}
              onToggle={emoji => toggleMarketingRequestCommentReaction(c.id!, emoji)}
              getUserDisplayName={getUserDisplayName}
              revealOnHover
            />
          )}
          {c.attachments && c.attachments.length > 0 && renderAttachments(c.attachments)}
        </div>
      </div>
    );
  };

  const sendDisabled =
    isSubmitting || ((!draftHtml || draftHtml === '<p></p>') && attachments.length === 0);

  const popoutTitleLabel = requestTitle
    ? (requestTitle.length > 72 ? `${requestTitle.slice(0, 69)}…` : requestTitle)
    : null;

  const body = (
    <div
      className={`tc-drop-zone ${fileDragOver ? 'tc-drop-zone--active' : ''}`}
      onDragEnter={handleFileDragEnter}
      onDragLeave={handleFileDragLeave}
      onDragOver={handleFileDragOver}
      onDrop={handleFileDrop}
    >
      {fileDragOver && (
        <div className="tc-drop-overlay" aria-hidden>
          <Paperclip size={28} strokeWidth={1.75} />
          <span>Drop files to attach</span>
        </div>
      )}

      {comments.length > 0 && (
        <div className="tc-filter-chips" aria-label="Filter comments">
          <div className="tc-filter-chip-buttons" role="tablist">
            {SCOPE_FILTER_CHIPS.map(f => (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`tc-filter-chip${filter === f.id ? ' tc-filter-chip--active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <select
            className={`tc-filter-chip tc-filter-mine-select${filter === 'mine' ? ' tc-filter-chip--active' : ''}`}
            aria-label="Filter by comment author"
            value={filter === 'mine' ? (mineAuthorId || user?.id || '') : ''}
            onChange={e => {
              const v = e.target.value;
              if (!v) {
                setFilter('all');
                return;
              }
              setMineAuthorId(v);
              setFilter('mine');
            }}
          >
            <option value="">Author</option>
            {user?.id ? <option value={user.id}>Mine</option> : null}
            {authorFilterOptions
              .filter(o => o.value !== user?.id)
              .map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
          </select>
          <div className="tc-filter-trailing">
            <select
              className="tc-filter-kind"
              value={kindFilter}
              onChange={e => setKindFilter(e.target.value as TaskCommentKind | 'any')}
              aria-label="Filter by kind"
            >
              <option value="any">All kinds</option>
              {KIND_OPTIONS.map(k => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="tc-feed" ref={scrollRef}>
        {visibleTopLevelComments.length === 0 && (
          <div className="tc-empty">
            {comments.length === 0
              ? 'No comments yet. Start the conversation.'
              : 'No comments match the current filters.'}
          </div>
        )}
        {visibleTopLevelComments.map((c, idx) => {
          const visibleReplies = (c.id ? repliesByThreadRoot.get(c.id) || [] : [])
            .map(r => comments.find(x => x.id === r.id))
            .filter((x): x is MarketingRequestComment => !!x && commentMatchesFilter(x));
          return (
            <React.Fragment key={c.id}>
              {renderCommentItem(c, { idx })}
              {visibleReplies.map((r, ri) => renderCommentItem(r, { indent: true, idx: idx + ri + 1 }))}
              {c.kind === 'question' && !commentsLocked && c.id ? (
                <QuestionAnswerSlot
                  questionId={c.id}
                  hasReplies={visibleReplies.length > 0}
                  mentionableUsers={mentionableUsers}
                  taskId={requestId}
                  disabled={isSubmitting}
                  onSubmit={async payload => {
                    await submitNewComment(payload, { replyToOverride: c, source: 'answer' });
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      {showCommentsOpenHelper && commentsPopoutOpen && (
        <CommentsOpenHelper variant="ops" onDismiss={dismissCommentsOpenHelper} />
      )}

      {commentsLocked ? (
        <div className="tc-composer-locked" role="status">
          Comments are closed on completed or cancelled requests. Change the status to add a comment.
        </div>
      ) : (
        <form className="tc-composer" onSubmit={handleSubmit}>
          <CommentTypingIndicator label={typingLabel} />
          {replyTo && (
            <div className="tc-reply-chip">
              <CornerDownRight size={12} />
              <span className="tc-reply-chip-label">Replying to <strong>{replyTo.userName}</strong>:</span>
              <span className="tc-reply-chip-excerpt">{excerptOf(replyTo)}</span>
              <button
                type="button"
                className="tc-reply-chip-clear"
                onClick={() => setReplyTo(null)}
                title="Cancel reply"
                aria-label="Cancel reply"
              >
                <X size={12} />
              </button>
            </div>
          )}

          {attachments.length > 0 && (
            <div className="tc-composer-attachments">
              {attachments.map((f, i) => (
                <div key={`${f.name}-${i}`} className="tc-staged-file">
                  <span className="tc-staged-icon">{getFileIcon(f.type)}</span>
                  <span className="tc-staged-name">{f.name}</span>
                  <button type="button" className="tc-staged-remove" onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}>×</button>
                </div>
              ))}
            </div>
          )}

          {uploadProgress > 0 && uploadProgress < 100 && (
            <div className="tc-upload-bar">
              <div className="tc-upload-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}

          <RichCommentEditor
            ref={editorRef}
            initialHtml={initialDraftHtml}
            mentionableUsers={mentionableUsers}
            taskId={requestId}
            onChange={setDraftHtml}
            onSubmit={handleEditorSubmitShortcut}
            placeholder={
              requestTitle
                ? `Write a comment on “${popoutTitleLabel}”…`
                : 'Write a comment… (@ to mention, ⌘/Ctrl-Enter to send)'
            }
            disabled={isSubmitting}
          />

          <div className="tc-composer-row">
            <button
              type="button"
              className={`tc-question-toggle${composerKind === 'question' ? ' tc-question-toggle--on' : ''}`}
              onClick={() => setComposerKind(k => (k === 'question' ? null : 'question'))}
              aria-pressed={composerKind === 'question'}
              title="Ask a question — anyone in this thread can answer underneath"
            >
              Question
            </button>
            <select
              className="tc-kind-select"
              value={composerKind ?? ''}
              onChange={e => setComposerKind((e.target.value || null) as TaskCommentKind | null)}
              aria-label="Comment kind"
              title="Tag this comment"
            >
              <option value="">No tag</option>
              {KIND_OPTIONS.map(k => (
                <option key={k.id} value={k.id}>{k.label}</option>
              ))}
            </select>

            <div className="tc-composer-spacer" />

            <input
              type="file"
              ref={fileInputRef}
              className="tc-file-input"
              multiple
              accept="image/*,application/pdf,.pdf"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              className="tc-btn-attach"
              onClick={() => fileInputRef.current?.click()}
              title="Attach files or screenshots"
              disabled={isSubmitting}
            >
              <Plus size={14} />
            </button>
            <button
              type="submit"
              className="tc-btn-send"
              title={isSubmitting ? 'Sending…' : 'Send (⌘/Ctrl-Enter)'}
              disabled={sendDisabled}
            >
              <Send size={14} />
            </button>
          </div>
        </form>
      )}
    </div>
  );

  const sectionClass = ['tc-section', isPopout ? 'tc-section--popout' : ''].filter(Boolean).join(' ');

  const shell = (
    <>
      {isPopout && (
        <div
          className="tc-popout-backdrop"
          onClick={() => setIsPopout(false)}
          aria-hidden
        />
      )}
      <div className={sectionClass}>
        {isPopout && (
          <div className="tc-popout-header">
            <span className="tc-popout-title" title={requestTitle}>
              {popoutTitleLabel ? `Comments · ${popoutTitleLabel}` : 'Comments'}
            </span>
            <span className="tc-popout-count">{comments.length}</span>
            <button
              type="button"
              className="tc-popout-close"
              onClick={() => setIsPopout(false)}
              title="Close (Esc)"
              aria-label="Close popup"
            >
              <X size={14} />
            </button>
          </div>
        )}
        {body}
      </div>
    </>
  );

  if (embedded) {
    return body;
  }

  if (isPopout) {
    return createPortal(shell, document.body);
  }

  return shell;
};

export default MarketingRequestComments;
